import type {
    FieldCondition,
    FieldOption,
    FieldValidator,
    FormBuilderConfig,
    FormElementConfig,
    FormFieldValue,
    FormValues,
} from '@sp-saljapro/form-builder';

import type {
    DtoGetOrderQuestionnairesResponse,
    DtoGetQuestion,
    DtoGetQuestionnaire,
    DtoGetServiceItemQuestionnaire,
} from '../generated';

export interface QuestionnaireFormTranslations {
    readonly freeTextLabel: string;
    readonly freeTextOptional: string;
    readonly freeTextRequired: string;
    readonly questionnaireTypeLabel: string;
    readonly optionYes: string;
    readonly optionNo: string;
    readonly optionNotApplicable: string;
    readonly additionalInfoSp: string;
    readonly additionalInfoTsp: string;
    readonly delAddressInRestrictedTrafficZone: string;
    readonly delAddressPartTimeAccessible: string;
    readonly distanceToParking: string;
    readonly doorCode: string;
    readonly doorWideEnough: string;
    readonly electricityAvailable: string;
    readonly elevatorExists: string;
    readonly externalElevator: string;
    readonly floorAndWallsFinished: string;
    readonly floorNo: string;
    readonly furnitureToBeRemoved: string;
    readonly islandFerry: string;
    readonly kitchenChecklistSigned: string;
    readonly mergedWithOrder: string;
    readonly na: string;
    readonly nameOfDoorBell: string;
    readonly parkingAvailable: string;
    readonly parkingPermitNeeded: string;
    readonly roomPrepared: string;
    readonly selectorBusinessCustomer: string;
    readonly selectorFlat: string;
    readonly selectorHouse: string;
    readonly stairCaseCurved: string;
    readonly stairCaseSize: string;
    readonly stairCaseWideEnough: string;
    readonly transportConnected: string;
    readonly truckDeliveryRestrictions: string;
    readonly typeOfWhiteGoods: string;
    readonly yes: string;
}

interface BuildQuestionnaireFormConfigParams {
    readonly orderQuestionnaires: DtoGetOrderQuestionnairesResponse;
    readonly requiredFieldMessage: string;
    readonly translations: QuestionnaireFormTranslations;
}

export type QuestionnaireSectionSource = 'delivery' | 'service';

interface BuildQuestionnaireSectionFormConfigParams extends BuildQuestionnaireFormConfigParams {
    readonly source: QuestionnaireSectionSource;
    readonly renderQuestionnaireType?: boolean;
}

interface QuestionnaireSectionFormConfigs {
    readonly deliveryConfig: FormBuilderConfig;
    readonly serviceConfig: FormBuilderConfig;
}

interface QuestionnaireOption extends FieldOption {
    readonly isFreeTextRequired: boolean;
}

const OPTION_SORT_ORDER = ['YES', 'NO', 'NA'];

const QUESTION_REFERENCE_TO_TRANSLATION_KEY: Partial<Record<string, keyof QuestionnaireFormTranslations>> = {
    ADDITIONAL_INFO_SP: 'additionalInfoSp',
    ADDITIONAL_INFO_TSP: 'additionalInfoTsp',
    DEL_ADDRESS_IN_RESTRICTED_TRAFFIC_ZONE: 'delAddressInRestrictedTrafficZone',
    DEL_ADDRESS_PART_TIME_ACCESSIBLE: 'delAddressPartTimeAccessible',
    DISTANCE_TO_PARKING: 'distanceToParking',
    DOOR_CODE: 'doorCode',
    DOOR_WIDE_ENOUGH: 'doorWideEnough',
    ELECTRICITY_AVAILABLE: 'electricityAvailable',
    ELEVATOR_EXISTS: 'elevatorExists',
    EXTERNAL_ELEVATOR: 'externalElevator',
    FLOOR_AND_WALLS_FINISHED: 'floorAndWallsFinished',
    FLOOR_NO: 'floorNo',
    FURNITURE_TO_BE_REMOVED: 'furnitureToBeRemoved',
    ISLAND_FERRY: 'islandFerry',
    KITCHEN_CHECKLIST_SIGNED: 'kitchenChecklistSigned',
    MERGED_WITH_ORDER: 'mergedWithOrder',
    NA: 'na',
    NAME_OF_DOOR_BELL: 'nameOfDoorBell',
    PARKING_AVAILABLE: 'parkingAvailable',
    PARKING_PERMIT_NEEDED: 'parkingPermitNeeded',
    ROOM_PREPARED: 'roomPrepared',
    SELECTOR_BUSINESS_CUSTOMER: 'selectorBusinessCustomer',
    SELECTOR_FLAT: 'selectorFlat',
    SELECTOR_HOUSE: 'selectorHouse',
    STAIR_CASE_CURVED: 'stairCaseCurved',
    STAIR_CASE_SIZE: 'stairCaseSize',
    STAIR_CASE_WIDE_ENOUGH: 'stairCaseWideEnough',
    TRANSPORT_CONNECTED: 'transportConnected',
    TRUCK_DELIVERY_RESTRICTIONS: 'truckDeliveryRestrictions',
    TYPE_OF_WHITE_GOODS: 'typeOfWhiteGoods',
    YES: 'yes',
};

const getQuestionLabel = (question: DtoGetQuestion, translations: QuestionnaireFormTranslations): string => {
    if (!question.questionReference) {
        return '';
    }

    const translationKey = QUESTION_REFERENCE_TO_TRANSLATION_KEY[question.questionReference];
    if (!translationKey) {
        return question.questionReferenceTranslated ?? question.questionReference;
    }

    return translations[translationKey];
};

const getQuestionFieldId = ({
    source,
    serviceItem,
    serviceItemIndex,
    questionnaire,
    questionnaireIndex,
    question,
}: {
    readonly source: string;
    readonly serviceItem: DtoGetServiceItemQuestionnaire;
    readonly serviceItemIndex: number;
    readonly questionnaire: DtoGetQuestionnaire;
    readonly questionnaireIndex: number;
    readonly question: DtoGetQuestion;
}): string => {
    return [
        source,
        serviceItem.serviceItemLineId ?? serviceItemIndex,
        questionnaire.questionnaireId ?? questionnaireIndex,
        question.questionReference,
    ].join('_');
};

const getQuestionnaireTypeFieldId = ({
    source,
    serviceItem,
    serviceItemIndex,
    questionnaire,
    questionnaireIndex,
}: {
    readonly source: string;
    readonly serviceItem: DtoGetServiceItemQuestionnaire;
    readonly serviceItemIndex: number;
    readonly questionnaire: DtoGetQuestionnaire;
    readonly questionnaireIndex: number;
}): string => {
    return [
        source,
        serviceItem.serviceItemLineId ?? serviceItemIndex,
        questionnaire.questionnaireId ?? questionnaireIndex,
        'questionnaireType',
    ].join('_');
};

const getOptionLabel = (value: string, translations: QuestionnaireFormTranslations): string => {
    switch (value) {
        case 'YES': {
            return translations.optionYes;
        }
        case 'NO': {
            return translations.optionNo;
        }
        case 'NA': {
            return translations.optionNotApplicable;
        }
        default: {
            return value;
        }
    }
};

const getQuestionnaireTypeLabel = (
    questionnaireType: string,
    questionnaireTypeTranslated: string | undefined,
    translations: QuestionnaireFormTranslations
): string => {
    switch (questionnaireType.toUpperCase()) {
        case 'BUSINESS_CUSTOMER': {
            return translations.selectorBusinessCustomer;
        }
        case 'FLAT': {
            return translations.selectorFlat;
        }
        case 'HOUSE': {
            return translations.selectorHouse;
        }
        default: {
            return questionnaireTypeTranslated ?? questionnaireType;
        }
    }
};

const getOptionSortOrder = (value: string | number): number => {
    const index = OPTION_SORT_ORDER.indexOf(String(value));
    return index === -1 ? OPTION_SORT_ORDER.length : index;
};

const toFieldOption = (option: QuestionnaireOption): FieldOption => ({
    value: option.value,
    label: option.label,
});

const getQuestionOptions = (
    question: DtoGetQuestion,
    translations: QuestionnaireFormTranslations
): QuestionnaireOption[] => {
    return (
        question.options?.flatMap((option) => {
            if (!option.value || option.value === 'UNDEFINED') {
                return [];
            }

            return [
                {
                    value: option.value,
                    label: getOptionLabel(option.value, translations),
                    isFreeTextRequired: option.isFreeTextRequired ?? false,
                },
            ];
        }) ?? []
    ).sort((a, b) => getOptionSortOrder(a.value) - getOptionSortOrder(b.value));
};

const isOptionSelected = (value: FormFieldValue | undefined, optionValue: string | number): boolean => {
    if (Array.isArray(value)) {
        return value.includes(optionValue);
    }

    return value === optionValue;
};

const getFreeTextRequiredCondition = (
    question: DtoGetQuestion,
    fieldId: string,
    options: readonly QuestionnaireOption[]
): FieldCondition | undefined => {
    const freeTextRequiredOptionValues = options
        .filter((option) => option.isFreeTextRequired)
        .map((option) => option.value);
    const hiddenUndefinedRequiresFreeText =
        question.options?.some((option) => option.value === 'UNDEFINED' && option.isFreeTextRequired) ?? false;

    if (freeTextRequiredOptionValues.length === 0 && !(hiddenUndefinedRequiresFreeText && options.length === 1)) {
        return undefined;
    }

    return {
        type: 'custom',
        dependsOn: [fieldId],
        resolver: (formValues) => {
            const value = formValues[fieldId];

            if (freeTextRequiredOptionValues.some((optionValue) => isOptionSelected(value, optionValue))) {
                return true;
            }

            if (!hiddenUndefinedRequiresFreeText || options.length !== 1) {
                return false;
            }

            const firstOption = options[0];
            if (!firstOption) {
                return false;
            }

            return !isOptionSelected(value, firstOption.value);
        },
    };
};

const hasRequiredHiddenFreeTextOption = (question: DtoGetQuestion): boolean => {
    return question.options?.some((option) => option.value === 'UNDEFINED' && option.isFreeTextRequired) ?? false;
};

const shouldRenderFreeTextOnlyElement = (question: DtoGetQuestion): boolean => {
    return hasRequiredHiddenFreeTextOption(question) || Boolean(question.answer?.freeTextAnswer);
};

const getFreeTextValidators = ({
    condition,
    requiredFieldMessage,
}: {
    readonly condition: FieldCondition | undefined;
    readonly requiredFieldMessage: string;
}): readonly FieldValidator[] => {
    if (!condition) {
        return [];
    }

    return [
        {
            type: 'required',
            message: requiredFieldMessage,
            condition,
        },
    ];
};

const getSelectionValidators = (
    question: DtoGetQuestion,
    requiredFieldMessage: string,
    selectionType: 'radio' | 'checkbox-group'
): readonly FieldValidator[] => {
    if (selectionType === 'checkbox-group') {
        return [];
    }

    if (!question.isMandatory) {
        return [];
    }

    return [
        {
            type: 'required',
            message: requiredFieldMessage,
        },
    ];
};

const toSelectionElement = ({
    fieldId,
    label,
    group,
    options,
    question,
    requiredFieldMessage,
}: {
    readonly fieldId: string;
    readonly label: string;
    readonly group: string;
    readonly options: readonly QuestionnaireOption[];
    readonly question: DtoGetQuestion;
    readonly requiredFieldMessage: string;
}): FormElementConfig => {
    if (options.length === 1) {
        return {
            id: fieldId,
            type: 'checkbox-group',
            label,
            group,
            options: options.map(toFieldOption),
            validators: getSelectionValidators(question, requiredFieldMessage, 'checkbox-group'),
        };
    }

    return {
        id: fieldId,
        type: 'radio',
        label,
        group,
        options: options.map(toFieldOption),
        validators: getSelectionValidators(question, requiredFieldMessage, 'radio'),
    };
};

const setSelectedDefaultValue = ({
    defaultValues,
    fieldId,
    selectionElement,
    selectedOption,
}: {
    readonly defaultValues: FormValues;
    readonly fieldId: string;
    readonly selectionElement: FormElementConfig;
    readonly selectedOption: QuestionnaireOption | undefined;
}): void => {
    if (selectedOption && selectionElement.type === 'checkbox-group') {
        defaultValues[fieldId] = [selectedOption.value];
    }

    if (selectedOption && selectionElement.type === 'radio') {
        defaultValues[fieldId] = selectedOption.value;
    }
};

const getFreeTextElement = ({
    fieldId,
    group,
    question,
    options,
    requiredFieldMessage,
    translations,
}: {
    readonly fieldId: string;
    readonly group: string;
    readonly question: DtoGetQuestion;
    readonly options: readonly QuestionnaireOption[];
    readonly requiredFieldMessage: string;
    readonly translations: QuestionnaireFormTranslations;
}): FormElementConfig => {
    const freeTextRequiredCondition = getFreeTextRequiredCondition(question, fieldId, options);

    return {
        id: `${fieldId}_freeText`,
        type: 'textarea',
        label: translations.freeTextLabel,
        helperText: freeTextRequiredCondition
            ? [
                  {
                      condition: freeTextRequiredCondition,
                      value: translations.freeTextRequired,
                  },
                  {
                      condition: true,
                      value: translations.freeTextOptional,
                  },
              ]
            : translations.freeTextOptional,
        group,
        characterLimit: 250,
        validators: getFreeTextValidators({
            condition: freeTextRequiredCondition,
            requiredFieldMessage,
        }),
    };
};

const setFreeTextDefaultValue = (
    defaultValues: FormValues,
    freeTextFieldId: string,
    question: DtoGetQuestion
): void => {
    if (question.answer?.freeTextAnswer) {
        defaultValues[freeTextFieldId] = question.answer.freeTextAnswer;
    }
};

const getFreeTextOnlyElement = ({
    fieldId,
    label,
    question,
    requiredFieldMessage,
    translations,
}: {
    readonly fieldId: string;
    readonly label: string;
    readonly question: DtoGetQuestion;
    readonly requiredFieldMessage: string;
    readonly translations: QuestionnaireFormTranslations;
}): FormElementConfig => {
    const isRequired = Boolean(question.isMandatory) || hasRequiredHiddenFreeTextOption(question);

    return {
        id: `${fieldId}_freeText`,
        type: 'textarea',
        label,
        helperText: isRequired ? translations.freeTextRequired : translations.freeTextOptional,
        characterLimit: 250,
        validators: isRequired
            ? [
                  {
                      type: 'required',
                      message: requiredFieldMessage,
                  },
              ]
            : [],
    };
};

const getQuestionElements = ({
    source,
    serviceItem,
    serviceItemIndex,
    questionnaire,
    questionnaireIndex,
    question,
    requiredFieldMessage,
    translations,
    defaultValues,
    renderedQuestionReferences,
}: {
    readonly source: string;
    readonly serviceItem: DtoGetServiceItemQuestionnaire;
    readonly serviceItemIndex: number;
    readonly questionnaire: DtoGetQuestionnaire;
    readonly questionnaireIndex: number;
    readonly question: DtoGetQuestion;
    readonly requiredFieldMessage: string;
    readonly translations: QuestionnaireFormTranslations;
    readonly defaultValues: FormValues;
    readonly renderedQuestionReferences: Set<string>;
}): FormElementConfig[] => {
    if (!question.questionReference) {
        return [];
    }

    if (renderedQuestionReferences.has(question.questionReference)) {
        return [];
    }

    const fieldId = getQuestionFieldId({
        source,
        serviceItem,
        serviceItemIndex,
        questionnaire,
        questionnaireIndex,
        question,
    });
    const label = getQuestionLabel(question, translations);
    const options = getQuestionOptions(question, translations);

    if (options.length === 0) {
        if (!shouldRenderFreeTextOnlyElement(question)) {
            return [];
        }

        const freeTextElement = getFreeTextOnlyElement({
            fieldId,
            label,
            question,
            requiredFieldMessage,
            translations,
        });
        setFreeTextDefaultValue(defaultValues, freeTextElement.id, question);
        renderedQuestionReferences.add(question.questionReference);

        return [freeTextElement];
    }

    const group = fieldId;
    const selectionElement = toSelectionElement({
        fieldId,
        label,
        group,
        options,
        question,
        requiredFieldMessage,
    });
    const freeTextElement = getFreeTextElement({
        fieldId,
        group,
        question,
        options,
        requiredFieldMessage,
        translations,
    });
    const elements: FormElementConfig[] = [selectionElement, freeTextElement];
    const selectedOption = options.find((option) => option.value === question.answer?.optionAnswer);

    setSelectedDefaultValue({
        defaultValues,
        fieldId,
        selectionElement,
        selectedOption,
    });
    setFreeTextDefaultValue(defaultValues, freeTextElement.id, question);
    renderedQuestionReferences.add(question.questionReference);

    return elements;
};

const getQuestionnaireTypeElement = ({
    source,
    serviceItem,
    serviceItemIndex,
    questionnaire,
    questionnaireIndex,
    translations,
    defaultValues,
}: {
    readonly source: string;
    readonly serviceItem: DtoGetServiceItemQuestionnaire;
    readonly serviceItemIndex: number;
    readonly questionnaire: DtoGetQuestionnaire;
    readonly questionnaireIndex: number;
    readonly translations: QuestionnaireFormTranslations;
    readonly defaultValues: FormValues;
}): FormElementConfig | undefined => {
    if (!questionnaire.questionnaireType) {
        return undefined;
    }

    const fieldId = getQuestionnaireTypeFieldId({
        source,
        serviceItem,
        serviceItemIndex,
        questionnaire,
        questionnaireIndex,
    });
    defaultValues[fieldId] = questionnaire.questionnaireType;

    return {
        id: fieldId,
        type: 'radio',
        label: translations.questionnaireTypeLabel,
        options: [
            {
                value: questionnaire.questionnaireType,
                label: getQuestionnaireTypeLabel(
                    questionnaire.questionnaireType,
                    questionnaire.questionnaireTypeTranslated,
                    translations
                ),
            },
        ],
    };
};

const getServiceItemElements = ({
    source,
    serviceItem,
    serviceItemIndex,
    renderQuestionnaireType,
    requiredFieldMessage,
    translations,
    defaultValues,
    renderedQuestionReferences,
}: {
    readonly source: string;
    readonly serviceItem: DtoGetServiceItemQuestionnaire;
    readonly serviceItemIndex: number;
    readonly renderQuestionnaireType: boolean;
    readonly requiredFieldMessage: string;
    readonly translations: QuestionnaireFormTranslations;
    readonly defaultValues: FormValues;
    readonly renderedQuestionReferences: Set<string>;
}): FormElementConfig[] => {
    return (
        serviceItem.questionnaires?.flatMap((questionnaire, questionnaireIndex) => {
            const questionnaireTypeElement = renderQuestionnaireType
                ? getQuestionnaireTypeElement({
                      source,
                      serviceItem,
                      serviceItemIndex,
                      questionnaire,
                      questionnaireIndex,
                      translations,
                      defaultValues,
                  })
                : undefined;
            const questionElements =
                questionnaire.questions?.flatMap((question) =>
                    getQuestionElements({
                        source,
                        serviceItem,
                        serviceItemIndex,
                        questionnaire,
                        questionnaireIndex,
                        question,
                        requiredFieldMessage,
                        translations,
                        defaultValues,
                        renderedQuestionReferences,
                    })
                ) ?? [];

            if (!questionnaireTypeElement) {
                return questionElements;
            }

            return [questionnaireTypeElement, ...questionElements];
        }) ?? []
    );
};

const getServiceItemQuestionnaires = ({
    orderQuestionnaires,
    source,
}: {
    readonly orderQuestionnaires: DtoGetOrderQuestionnairesResponse;
    readonly source: QuestionnaireSectionSource;
}): readonly DtoGetServiceItemQuestionnaire[] => {
    if (source === 'delivery') {
        return orderQuestionnaires.deliveryQuestionnaires ?? [];
    }

    return orderQuestionnaires.serviceQuestionnaires ?? [];
};

const hasQuestionnaireTypeElement = (config: FormBuilderConfig): boolean =>
    config.elements.some((element) => element.id.endsWith('_questionnaireType'));

export const buildQuestionnaireSectionFormConfig = ({
    orderQuestionnaires,
    source,
    renderQuestionnaireType = true,
    requiredFieldMessage,
    translations,
}: BuildQuestionnaireSectionFormConfigParams): FormBuilderConfig => {
    const defaultValues: FormValues = {};
    const renderedQuestionReferences = new Set<string>();
    const elements = getServiceItemQuestionnaires({ orderQuestionnaires, source }).flatMap(
        (serviceItem, serviceItemIndex) =>
            getServiceItemElements({
                source,
                serviceItem,
                serviceItemIndex,
                renderQuestionnaireType,
                requiredFieldMessage,
                translations,
                defaultValues,
                renderedQuestionReferences,
            })
    );

    return {
        id: `${source}-order-questionnaires`,
        elements,
        defaultValues,
    };
};

export const buildQuestionnaireSectionFormConfigs = ({
    orderQuestionnaires,
    requiredFieldMessage,
    translations,
}: BuildQuestionnaireFormConfigParams): QuestionnaireSectionFormConfigs => {
    const deliveryConfig = buildQuestionnaireSectionFormConfig({
        orderQuestionnaires,
        source: 'delivery',
        requiredFieldMessage,
        translations,
    });
    const serviceConfig = buildQuestionnaireSectionFormConfig({
        orderQuestionnaires,
        source: 'service',
        renderQuestionnaireType: !hasQuestionnaireTypeElement(deliveryConfig),
        requiredFieldMessage,
        translations,
    });

    return {
        deliveryConfig,
        serviceConfig,
    };
};

export const buildQuestionnaireFormConfig = ({
    orderQuestionnaires,
    requiredFieldMessage,
    translations,
}: BuildQuestionnaireFormConfigParams): FormBuilderConfig => {
    const { deliveryConfig, serviceConfig } = buildQuestionnaireSectionFormConfigs({
        orderQuestionnaires,
        requiredFieldMessage,
        translations,
    });

    return {
        id: 'order-questionnaires',
        elements: [...deliveryConfig.elements, ...serviceConfig.elements],
        defaultValues: {
            ...deliveryConfig.defaultValues,
            ...serviceConfig.defaultValues,
        },
    };
};
